import { LoanType } from "@/modules/loan/common/enum";
import { CloudflareAsset } from "@/shared/common/enums";
import { Amount, CloudFlareImage } from "@/shared/components";
import { MoreIcon } from "@/shared/components/SvgIcons";
import { formatDate } from "@/shared/helpers";
import { useAppSelector, useMediaQuery } from "@/shared/hooks";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  index: number;
  name: string;
  endDate?: string;
  currency: string;
  currentDate: Date;
  className?: string;
  loanType?: LoanType;
  hasMoreAction?: boolean;
  contractNumber: string;
  upcomingPaymentAmount: number;
  remainingPrincipalAmount: number;
  upcomingPaymentDate: string;
  handleShowBalances: (i: number) => void;
}

export const LoansItem = ({
  index,
  endDate,
  loanType,
  currentDate,
  currency,
  className,
  hasMoreAction,
  contractNumber,
  upcomingPaymentAmount,
  upcomingPaymentDate,
  remainingPrincipalAmount,
  handleShowBalances,
}: Props) => {
  const isDesktop = useMediaQuery();
  const { t } = useTranslation();
  const showBalances = useAppSelector((state) => state.dashboard.showBalances);

  const onShowBalance = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    handleShowBalances(index);
  };

  const paymentDate = new Date(upcomingPaymentDate);
  const isDelayed = (currentDate > paymentDate) && upcomingPaymentAmount > 0;
  const isFactoring = loanType === LoanType.Factoring;

  return (
    <div className={`flexbox align-items--center pl-32 pr-32 widget-content-item ${className}`}>
      <div className="item-icon flexbox align-items--center justify-content--center">
        <div className="flexbox align-items--center justify-content--center">
          <CloudFlareImage path={CloudflareAsset.Loan}/>
        </div>
      </div>
      <div className="loan-item-content full-width flexbox flex-direction--column pl-16">
        {!!index && (
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        )}
        <div className="flexbox full-width pt-20 pb-20 loan-item-body-wrapper">
          <div className="flexbox flex-direction--column loan-item-body">
            <div className="full-width flexbox justify-content--between">
              <Text className="loan-item-title pr-16 truncate" size="medium" weight="semibold">
                {contractNumber}
              </Text>
              <div
                onClick={onShowBalance}
                className="flexbox justify-content--center align-items--end flex-direction--column pr-6"
              >
                <Amount
                  currency={currency}
                  amount={remainingPrincipalAmount}
                  isShow={showBalances[index]}
                  className="loan-item-amount"
                />
              </div>
            </div>
            <div className="full-width flexbox justify-content--between">
              <Text className="text-nowrap loan-item-payment-date truncate" type="tertiary">
                {t(isFactoring ? "loans.factoringEndDate" : isDesktop ? "loans.nextPaymentDate" : "loans.nextPayment")}:
                {" "}
                {formatDate(isFactoring ? endDate : upcomingPaymentDate)}
              </Text>
              <div
                onClick={onShowBalance}
                className="flexbox justify-content--center align-items--end flex-direction--column pr-6"
              >
                <Amount
                  type={isDelayed ? "danger-dark" : "tertiary"}
                  currency={currency}
                  size="standard"
                  className="loan-item-next-amount pl-16"
                  amount={upcomingPaymentAmount}
                  isShow={showBalances[index]}
                />
              </div>
            </div>
          </div>
          {hasMoreAction && (
            <div className="cursor-pointer flexbox align-items--center">
              <MoreIcon type="secondary"/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
